import { useCallback, useEffect, useRef, useState } from "react";
import type { EventContext } from "../context/event";
import { saveSettings } from "./save";
import {
  batches,
  pathsOf,
  isEmpty,
  purgePending,
  purgeUnset,
  queue,
  queueUnset,
  restore,
  restoreUnset,
  settle,
  withoutNoOps,
  type Pending,
  type PendingUnset,
  type SyncState,
} from "./sync";
import type { Extended } from "./types";
import type { Rows } from "./store";
import { ApiError } from "../api/client";

/** How long changes are allowed to sit before they leave, in automatic mode. */
const EVERY = 6_000;

/** How the organiser wants their work saved. Their choice, not the event's. */
export type SaveMode = "auto" | "manual";

const MODE_KEY = "gk-fo-v2-save-mode";

/**
 * MANUAL BY DEFAULT: nothing reaches the event until the organiser says so.
 *
 * The safer-looking default would be automatic — work cannot be lost that way.
 * But this panel writes to a LIVE event, and a value typed while thinking is
 * indistinguishable from one that was meant: automatic by default means a
 * half-finished sentence is on the site before anyone decided it should be.
 * What protects the work instead is the leave-warning below, which is loud
 * precisely because it is rare.
 *
 * The same answer when storage is unavailable: a default that changes depending
 * on the browser is not a default.
 */
export function readMode(): SaveMode {
  try {
    return localStorage.getItem(MODE_KEY) === "auto" ? "auto" : "manual";
  } catch {
    return "manual";
  }
}

export function writeMode(mode: SaveMode): void {
  try {
    localStorage.setItem(MODE_KEY, mode);
  } catch {
    /* the choice will not survive a reload; the session still obeys it */
  }
}

/**
 * SAVING, WITHOUT A SAVE BUTTON.
 *
 * Changes pile up and leave every six seconds. Sending each keystroke is a
 * request per letter; waiting for a button is how an afternoon disappears.
 *
 * ⚠️ IT ALSO FLUSHES WHEN THE PAGE IS HIDDEN, and that is not a refinement: on a
 * six-second cycle, closing the tab at the wrong moment loses up to six seconds
 * of work. `visibilitychange` is the one event that still fires reliably on
 * mobile, where `beforeunload` is routinely skipped.
 *
 * Nothing is saved in the seeded demonstration — there is no event to save to,
 * and inventing one would write somebody else's configuration.
 *
 * Displayed strings are French: the organiser is a French speaker.
 *
 * `server` est ce que l'événement tient à son chargement : la référence contre laquelle
 * la file se compare, voir `withoutNoOps`.
 */
export function useAutoSave(
  event: EventContext | null,
  mode: SaveMode,
  server: Rows | null = null,
) {
  const pending = useRef<Pending>({});
  const pendingUnset = useRef<PendingUnset>({});
  const inFlight = useRef(false);
  /** Ce que le serveur tient, tenu à jour à chaque lot accepté. */
  const saved = useRef<Rows>({});
  /** Le lot en route : tant qu'il n'a pas répondu, la référence l'inclut déjà. */
  const sending = useRef<{ pending: Pending; unset: PendingUnset } | null>(
    null,
  );
  /** L'état à retrouver quand la file se vide sans rien envoyer. */
  const settled = useRef<SyncState>("idle");
  /** Le serveur a refusé : plus d'envoi automatique jusqu'à une demande explicite. */
  const blocked = useRef(false);
  const [state, setState] = useState<SyncState>("idle");

  useEffect(() => {
    saved.current = server ?? {};
  }, [server]);

  const flush = useCallback(async (deliberate = false, keepalive = false) => {
    if (
      !event ||
      inFlight.current ||
      isEmpty(pending.current, pendingUnset.current) ||
      // Refusé une fois : plus rien ne part tout seul. Voir le `catch`.
      (blocked.current && !deliberate)
    )
      return;
    if (deliberate) blocked.current = false;

    const leaving = pending.current;
    const leavingUnset = pendingUnset.current;
    pending.current = {};
    pendingUnset.current = {};
    inFlight.current = true;
    sending.current = { pending: leaving, unset: leavingUnset };
    setState("saving");

    try {
      // Sequential, not parallel: the route reads then writes one row per
      // language, and two overlapping calls on the same row lose one of them.
      for (const batch of batches(leaving, leavingUnset)) {
        await saveSettings(
          event,
          batch.languageId,
          batch.extended,
          batch.unset,
          undefined,
          keepalive,
        );
      }
      saved.current = settle(saved.current, leaving, leavingUnset);
      settled.current = "saved";
      // Only "saved" if nothing arrived meanwhile — otherwise the badge would
      // claim everything is safe while a change is still waiting.
      setState(
        isEmpty(pending.current, pendingUnset.current) ? "saved" : "waiting",
      );
    } catch (cause) {
      // Put it back UNDERNEATH whatever was typed since — see `restore`.
      pending.current = restore(pending.current, leaving);
      pendingUnset.current = restoreUnset(pendingUnset.current, leavingUnset);
      /**
       * ⚠️ UN REFUS ARRÊTE LE CYCLE. `PUT /settings/fo` exige un rôle sur le
       * domaine : sans lui, réessayer toutes les six secondes ne fait qu'un appel
       * refusé toutes les six secondes, indéfiniment, en affichant « une nouvelle
       * tentative aura lieu » à quelqu'un pour qui elle n'aboutira pas. Les
       * modifications restent en mémoire — c'est le point de `restore` — et un
       * envoi MANUEL relance quand même, au cas où les droits auraient changé.
       */
      const refused = cause instanceof ApiError && cause.isUnauthorized;
      blocked.current = refused;
      setState(refused ? "refused" : "failed");
    } finally {
      inFlight.current = false;
      sending.current = null;
    }
  }, [event]);

  /**
   * ⚠️ IN MANUAL MODE, NOTHING LEAVES ON ITS OWN — no timer, no flush when the
   * page hides, none on unmount either. An organiser who asked to decide when
   * their work is sent would not expect it to go anyway the moment they switch
   * tabs. What protects them instead is the warning below.
   */
  const modeRef = useRef(mode);
  modeRef.current = mode;
  useEffect(() => {
    if (!event || mode !== "auto") return;
    const timer = window.setInterval(() => void flush(), EVERY);
    const onHide = () => {
      // keepalive : la requête doit survivre au déchargement, sinon le navigateur
      // l'avorte et le vidage de secours ne délivre rien (surtout sur mobile).
      if (document.visibilityState === "hidden") void flush(false, true);
    };
    document.addEventListener("visibilitychange", onHide);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onHide);
      // Leaving organiser mode, or the event changing, must not strand what is
      // still waiting. ⚠️ MAIS PAS LE PASSAGE EN MANUEL : ce nettoyage court aussi
      // quand `mode` change, et envoyait aussitôt ce que l'organisateur venait de
      // demander à garder. La ref porte le mode d'APRÈS le changement.
      if (modeRef.current === "auto") void flush();
    };
  }, [event, mode, flush]);

  /**
   * LEAVING WITH UNSAVED WORK gets the browser's own warning.
   *
   * Registered ONLY while something is actually waiting: a page that always asks
   * "are you sure?" teaches people to click through it, and then it protects
   * nothing on the day it matters.
   *
   * The message is the browser's — no page has been able to choose its own
   * wording for a decade, and pretending otherwise would be writing a string
   * nobody will ever read.
   */
  // « Refusé » compte comme non enregistré : le bouton d'envoi manuel doit rester
  // là (c'est la seule relance possible) et l'avertissement au départ de la page
  // doit continuer de prévenir qu'il reste des modifications à l'écran.
  const unsaved =
    state === "waiting" ||
    state === "saving" ||
    state === "failed" ||
    state === "refused";

  useEffect(() => {
    if (!event || !unsaved) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [event, unsaved]);

  /** Ce que le serveur tient, lot en route compris. */
  const stored = useCallback(
    (): Rows =>
      sending.current
        ? settle(saved.current, sending.current.pending, sending.current.unset)
        : saved.current,
    [],
  );

  /**
   * Après chaque geste : ce qui redevient égal au serveur sort de la file, et l'état dit
   * ce qui reste. File vide, plus rien n'attend : ni bouton, ni avertissement au départ.
   * Pendant un envoi, la référence inclut le lot en route, et c'est sa réponse qui
   * tranchera l'état.
   */
  const prune = useCallback(() => {
    const kept = withoutNoOps(
      pending.current,
      pendingUnset.current,
      stored(),
    );
    pending.current = kept.pending;
    pendingUnset.current = kept.unset;
    if (!isEmpty(kept.pending, kept.unset)) setState("waiting");
    else if (!inFlight.current) setState(settled.current);
  }, [stored]);

  const record = useCallback(
    (languageId: number, extended: Extended) => {
      if (!event) return;
      pending.current = queue(pending.current, languageId, extended);
      /* Une valeur écrite annule un effacement en attente de la même clé : sans ça, le
         lot partait avec la valeur ET son `unset`, et le serveur la supprimait après
         l'avoir fusionnée (« Reprendre l'héritée » puis se raviser). */
      pendingUnset.current = purgeUnset(
        pendingUnset.current,
        languageId,
        pathsOf(extended),
      );
      prune();
    },
    [event, prune],
  );

  const forget = useCallback(
    (languageId: number, paths: string[]) => {
      if (!event) return;
      pendingUnset.current = queueUnset(
        pendingUnset.current,
        languageId,
        paths,
      );
      /* Symétrique : un effacement annule une valeur en attente de la même clé, pour ne
         pas envoyer une valeur que le même lot supprime aussitôt. */
      pending.current = purgePending(pending.current, languageId, paths);
      prune();
    },
    [event, prune],
  );

  /**
   * Ce qu'une autre route a DÉJÀ enregistré (la traduction écrit elle-même) : la
   * référence doit le savoir, sinon revenir à l'ancienne valeur se croirait sans effet
   * et ne partirait pas, laissant au serveur celle que l'écran ne montre plus.
   */
  const learn = useCallback((languageId: number, extended: Extended) => {
    saved.current = settle(saved.current, { [languageId]: extended });
  }, []);

  return {
    state,
    record,
    forget,
    learn,
    stored,
    flush,
    unsaved,
    enabled: !!event,
  };
}
